import { z } from 'zod';
import { rectSchema } from './layout';

/**
 * The entity types a script-entities run looks for, in display order. `type` in `public.entities` is checked
 * against the same keys (a test reads the migration). Adding a type: here, a migration for the check, a playbook.
 */
export const ENTITY_TYPES = ['scene', 'character'] as const;
export const entityTypeSchema = z.enum(ENTITY_TYPES);
export type EntityType = z.infer<typeof entityTypeSchema>;

/** The categorical colour tokens of the design system an entity type may take. */
export const ENTITY_COLORS = [
  'entity-1',
  'entity-2',
  'entity-3',
  'entity-4',
  'entity-5',
  'entity-6',
] as const;
export type EntityColor = (typeof ENTITY_COLORS)[number];

/** How a field of a type's `extra` is shown and asked for. */
export type EntityField = {
  key: string;
  label: string;
  description: string;
  kind: 'text' | 'enum' | 'list';
  required: boolean;
  values?: readonly string[];
};

export type EntityTypeSpec = {
  key: EntityType;
  label: string;
  plural: string;
  description: string;
  color: EntityColor;
  /** Validates the `extra` column of this type. Strict: an unknown key is refused. */
  extra: z.ZodObject<z.ZodRawShape>;
  fields: EntityField[];
  /** A complete entity of this type, as the agent should write it. */
  example: Record<string, unknown>;
};

/** An entity's `key`: a slug, unique per script and type, chosen by the agent. Mirrors the SQL check. */
export const ENTITY_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

export const INTERIOR_EXTERIOR = ['INT', 'EXT', 'INT/EXT'] as const;

const characterExtra = z.strictObject({
  forename: z.string().min(1).max(200).optional(),
  surname: z.string().min(1).max(200).optional(),
  occupation: z.string().min(1).max(200).optional(),
});

const sceneExtra = z.strictObject({
  interior_exterior: z.enum(INTERIOR_EXTERIOR),
  time_of_day: z.array(z.string().min(1).max(50)),
});

export const entityRegistry: Record<EntityType, EntityTypeSpec> = {
  scene: {
    key: 'scene',
    label: 'Scene',
    plural: 'Scenes',
    description:
      'A place taken from the scene headings. One entity per place; its references are the place name in each heading, never a mention in prose.',
    color: 'entity-1',
    extra: sceneExtra,
    fields: [
      {
        key: 'interior_exterior',
        label: 'Interior or exterior',
        description: 'INT, EXT, or INT/EXT when the headings use both.',
        kind: 'enum',
        required: true,
        values: INTERIOR_EXTERIOR,
      },
      {
        key: 'time_of_day',
        label: 'Times of day',
        description: 'The distinct times of day the headings give for this place.',
        kind: 'list',
        required: true,
      },
    ],
    example: {
      key: 'johns-apartment',
      name: "JOHN'S APARTMENT",
      description: 'A fourth-floor walk-up where John lives alone.',
      defamatory_points: [],
      extra: { interior_exterior: 'INT', time_of_day: ['NIGHT', 'DAY'] },
      references: [{ line: 12, quote: "JOHN'S APARTMENT" }],
    },
  },
  character: {
    key: 'character',
    label: 'Character',
    plural: 'Characters',
    description:
      'A person who speaks or has a significant part on screen, named or not. Every cue line and every prose mention is a reference.',
    color: 'entity-2',
    extra: characterExtra,
    fields: [
      {
        key: 'forename',
        label: 'Forename',
        description: 'If the script gives one.',
        kind: 'text',
        required: false,
      },
      {
        key: 'surname',
        label: 'Surname',
        description: 'If the script gives one.',
        kind: 'text',
        required: false,
      },
      {
        key: 'occupation',
        label: 'Occupation',
        description: 'What the character does, if the script says.',
        kind: 'text',
        required: false,
      },
    ],
    example: {
      key: 'dana-reyes',
      name: 'DANA',
      description: 'A harbour pilot in her forties, the lead.',
      defamatory_points: ['Shown taking a bribe in scene 14.'],
      extra: { forename: 'Dana', surname: 'Reyes', occupation: 'Harbour pilot' },
      references: [
        { line: 31, quote: 'DANA' },
        { line: 34, quote: 'Dana steps off the launch' },
      ],
    },
  },
};

/** What a version says of an entity: its defamatory points (none is an empty list) and what changed (null when nothing material). */
const defamatoryPointsSchema = z.array(z.string().min(1).max(2000)).max(50);
const versionUpdatesSchema = z.string().max(2000).nullable();

/**
 * The columns every entity has, as the agent writes them. `defamatory_points` and `version_updates` are this
 * version's; `first_version`, `fixed` (first found by an earlier version: its name, description and extra may
 * not change) and `history` (what the earlier versions said, newest first) are filled by `entities pull` and
 * ignored by a push.
 */
export const entityCoreSchema = z.object({
  key: z.string().regex(ENTITY_KEY_PATTERN),
  name: z.string().min(1).max(200),
  description: z.string().max(4000).default(''),
  defamatory_points: defamatoryPointsSchema.default([]),
  version_updates: versionUpdatesSchema.default(null),
  first_version: z.number().int().positive().nullable().default(null),
  fixed: z.boolean().default(false),
  history: z
    .array(
      z.object({
        version: z.number().int().positive(),
        defamatory_points: defamatoryPointsSchema.nullable(),
        version_updates: versionUpdatesSchema,
      }),
    )
    .default([]),
});
export type EntityCore = z.infer<typeof entityCoreSchema>;
/** The entity fields `entities pull` fills in and a push ignores. */
export const ENTITY_READ_ONLY_FIELDS = ['first_version', 'fixed', 'history'] as const;

/**
 * A reference as the agent cites it (line and quote), optionally already resolved by the CLI (page, span,
 * rects). `push` refuses one that is not resolved.
 */
export const entityReferenceSchema = z.object({
  line: z.number().int().positive(),
  quote: z.string().min(1).max(2000),
  page: z.number().int().positive().optional(),
  span: z
    .tuple([
      z.number().int().positive(),
      z.number().int().nonnegative(),
      z.number().int().positive(),
      z.number().int().nonnegative(),
    ])
    .optional(),
  rects: z.array(rectSchema).min(1).optional(),
});
export type EntityReference = z.infer<typeof entityReferenceSchema>;

export const entityEntrySchema = entityCoreSchema.extend({
  extra: z.record(z.string(), z.unknown()).default({}),
  references: z.array(entityReferenceSchema).default([]),
});
export type EntityEntry = z.infer<typeof entityEntrySchema>;

/**
 * One YAML file per type: its entities with their references nested (a reference cannot exist without its
 * entity). `extra` is validated against the type's schema from the registry.
 */
export const entityYamlSchema = z
  .object({
    type: entityTypeSchema,
    entities: z.array(entityEntrySchema),
  })
  .superRefine((file, ctx) => {
    const spec = entityRegistry[file.type];
    const keys = new Set<string>();
    file.entities.forEach((entity, i) => {
      if (keys.has(entity.key)) {
        ctx.addIssue({
          code: 'custom',
          message: `duplicate key ${entity.key}`,
          path: ['entities', i, 'key'],
        });
      }
      keys.add(entity.key);
      const result = spec.extra.safeParse(entity.extra);
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({ ...issue, path: ['entities', i, 'extra', ...issue.path] });
        }
      }
    });
  });
export type EntityYaml = z.infer<typeof entityYamlSchema>;
